import { DEFAULT_GAME_SETTINGS, store, } from "../app/state/store.js";
import { labelFor, registerCustomIcon, registerCustomLabel, } from "../theme/naming.js";
import { iconFor } from "../theme/icons.js";
import { loadCustomPhonemes, loadHiddenPresets, loadPresetOverrides, loadTrainedPhonemes, } from "../storage/phonemes.js";
import { getActiveLibrary } from "../storage/libraries.js";
import { escapeHtml } from "../game/util/escape.js";
const PRESET_LANES = ["hiss", "airy", "pop", "hum"];
// Quick-select tolerance & scoring bundles. Each preset only overrides the
// tolerance/scoring-relevant fields; sound selection and spawn shape are left
// to the player.
const DIFFICULTY_PRESETS = [
    {
        id: "relaxed",
        label: "Relaxed",
        icon: "🌱",
        settings: {
            speed: 0.5,
            windowMs: 320,
            perfectMs: 90,
            basePoints: 120,
            comboCap: 30,
        },
    },
    {
        id: "normal",
        label: "Normal",
        icon: "⚖️",
        settings: {
            speed: 0.7,
            windowMs: 220,
            perfectMs: 45,
            basePoints: 100,
            comboCap: 20,
        },
    },
    {
        id: "sharp",
        label: "Sharp",
        icon: "⚡",
        settings: {
            speed: 0.9,
            windowMs: 150,
            perfectMs: 30,
            basePoints: 100,
            comboCap: 20,
        },
    },
    {
        id: "expert",
        label: "Expert",
        icon: "🔥",
        settings: {
            speed: 1.2,
            windowMs: 100,
            perfectMs: 20,
            basePoints: 90,
            comboCap: 15,
        },
    },
];
export class AcGameSettings extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "categories", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: [...PRESET_LANES]
        });
        Object.defineProperty(this, "unsub", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "suppressRerender", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "trained", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
    }
    connectedCallback() {
        this.style.display = "block";
        void this.loadLibrary().then(() => this.render());
        this.unsub = store.gameSettings.subscribe(() => this.render(), false);
    }
    disconnectedCallback() {
        this.unsub?.();
        this.unsub = null;
    }
    async loadLibrary() {
        let activeIsDefault = true;
        let activePhonemeIds = [];
        try {
            const active = await getActiveLibrary();
            activeIsDefault = active.id === "default";
            activePhonemeIds = active.phonemes.map((p) => p.id);
            for (const p of active.phonemes) {
                registerCustomLabel(p.id, p.label);
                registerCustomIcon(p.id, p.icon);
            }
        }
        catch { }
        let hidden = [];
        let custom = [];
        try {
            hidden = await loadHiddenPresets();
        }
        catch { }
        try {
            custom = await loadCustomPhonemes();
        }
        catch { }
        try {
            const overrides = await loadPresetOverrides();
            for (const [id, ov] of Object.entries(overrides)) {
                if (ov.label)
                    registerCustomLabel(id, ov.label);
                if (ov.icon)
                    registerCustomIcon(id, ov.icon);
            }
        }
        catch { }
        try {
            const trainedIds = await loadTrainedPhonemes();
            this.trained = new Set(trainedIds.filter((p) => p.trained).map((p) => p.id));
        }
        catch {
            this.trained = new Set();
        }
        for (const p of custom) {
            registerCustomLabel(p.id, p.label);
            registerCustomIcon(p.id, p.icon);
        }
        if (activeIsDefault) {
            const visiblePresets = PRESET_LANES.filter((id) => !hidden.includes(id));
            this.categories = [...visiblePresets, ...custom.map((p) => p.id)];
        }
        else {
            this.categories = [...activePhonemeIds];
        }
        const settings = store.gameSettings.get();
        const enabled = { ...settings.enabled };
        let changed = false;
        for (const id of this.categories) {
            if (enabled[id] === undefined) {
                enabled[id] = this.trained.has(id);
                changed = true;
            }
            else if (enabled[id] && !this.trained.has(id)) {
                enabled[id] = false;
                changed = true;
            }
        }
        if (changed)
            store.gameSettings.update((s) => ({ ...s, enabled }));
    }
    patch(p) {
        store.gameSettings.update((s) => ({ ...s, ...p }));
    }
    activePresetId(s) {
        for (const preset of DIFFICULTY_PRESETS) {
            const keys = Object.keys(preset.settings);
            if (keys.every((k) => s[k] === preset.settings[k])) {
                return preset.id;
            }
        }
        return null;
    }
    render() {
        if (this.suppressRerender)
            return;
        const s = store.gameSettings.get();
        const theme = "child";
        const fmts = {};
        const toggles = this.categories
            .map((id) => {
            const on = s.enabled[id] ?? true;
            const trained = this.trained.has(id);
            const trainMark = trained
                ? `<span style="font-size:10px;color:#4ade80">✓ trained</span>`
                : `<span style="font-size:10px;color:#fbbf24">untrained</span>`;
            const clickable = trained;
            return `<div data-cat="${id}" style="display:flex;align-items:center;
                    gap:8px;padding:8px 12px;border-radius:10px;
                    cursor:${clickable ? "pointer" : "not-allowed"};
                    pointer-events:${clickable ? "auto" : "none"};
                    border:2px solid ${on ? "#22d3ee" : "#2a2f4a"};
                    background:${on ? "#12324a" : "#12162a"};
                    opacity:${clickable ? (on ? "1" : ".6") : ".4"}">
                    <span style="font-size:18px">${escapeHtml(iconFor(id, theme))}</span>
                    <span style="display:flex;flex-direction:column">
                      <span style="font-size:13px">${escapeHtml(labelFor(id, theme))}</span>
                      ${trainMark}
                    </span>
                    <span style="margin-left:auto;font-size:12px">${on ? "✓" : "○"}</span>
                    </div>`;
        })
            .join("");
        const slider = (key, label, min, max, step, fmt) => {
            const v = s[key];
            fmts[key] = fmt;
            return `<div style="margin:10px 0">
                    <div style="display:flex;justify-content:space-between;font-size:12px;opacity:.85">
                      <span>${label}</span><span data-val="${key}" data-fmt="${key}">${fmt(v)}</span>
                    </div>
                    <input type="range" data-set="${key}" min="${min}" max="${max}"
                        step="${step}" value="${v}"
                        style="width:100%;margin-top:4px;pointer-events:auto"/>
                    </div>`;
        };
        const activePreset = this.activePresetId(s);
        const presetButtons = DIFFICULTY_PRESETS.map((preset) => {
            const on = activePreset === preset.id;
            return `<div data-preset="${preset.id}"
                    style="flex:1;min-width:80px;display:flex;flex-direction:column;
                    align-items:center;gap:4px;padding:10px 8px;border-radius:10px;
                    cursor:pointer;pointer-events:auto;
                    border:2px solid ${on ? "#22d3ee" : "#2a2f4a"};
                    background:${on ? "#12324a" : "#12162a"}">
                    <span style="font-size:20px">${preset.icon}</span>
                    <span style="font-size:12px;font-weight:600">${preset.label}</span>
                  </div>`;
        }).join("");
        this.innerHTML = `
              <div style="color:#fff">
                <h3 style="margin:0 0 8px">Sounds</h3>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">${toggles}</div>
                <h3 style="margin:16px 0 4px">Difficulty</h3>
                <div style="display:flex;flex-wrap:wrap;gap:8px">${presetButtons}</div>
                <h3 style="margin:16px 0 4px">Tuning</h3>
                ${slider("speed", "Speed", 0.2, 2, 0.05, (v) => `${Math.round(v * 100)}%`)}
                ${slider("length", "Run length", 4, 40, 1, (v) => String(Math.round(v)))}
                ${slider("windowMs", "Hit window", 80, 400, 10, (v) => `${Math.round(v)}ms`)}
                ${slider("perfectMs", "Perfect window", 15, 120, 5, (v) => `${Math.round(v)}ms`)}
                ${slider("basePoints", "Base points", 50, 300, 10, (v) => String(Math.round(v)))}
                ${slider("comboCap", "Combo cap", 5, 40, 1, (v) => `${Math.round(v)}x`)}
                 <h3 style="margin:16px 0 4px">Spawning & sustainment</h3>
                 ${slider("spawnIntervalBeats", "Spawn interval", 0.5, 4, 0.25, (v) => `${v.toFixed(2)} beats`)}
                ${slider("breakBeats", "Break period (min gap)", 0, 3, 1, (v) => `${Math.round(v)} beat${Math.round(v) === 1 ? "" : "s"}`)}
                 ${slider("sustainChance", "Sustain chance", 0, 1, 0.05, (v) => `${Math.round(v * 100)}%`)}
                     <div style="font-size:12px;opacity:.7;margin:8px 0 2px">
                       Sustain length distribution (relative weights)
                     </div>
                     ${slider("sustainWeight1", "1 beat", 0, 4, 0.1, (v) => v.toFixed(1))}
                     ${slider("sustainWeight2", "2 beats", 0, 4, 0.1, (v) => v.toFixed(1))}
                     ${slider("sustainWeight4", "4 beats", 0, 4, 0.1, (v) => v.toFixed(1))}
                     ${slider("sustainWeight8", "8 beats", 0, 4, 0.1, (v) => v.toFixed(1))}
                 <div data-infinite-row style="display:flex;align-items:center;
                     gap:10px;padding:10px 12px;border-radius:10px;cursor:pointer;
                     pointer-events:auto;margin-top:10px;
                     border:2px solid ${s.infinite ? "#22d3ee" : "#2a2f4a"};
                     background:${s.infinite ? "#12324a" : "#12162a"}">
                   <span style="flex:1;font-size:13px">Infinite game (never ends)</span>
                   <span style="font-size:14px">${s.infinite ? "✓" : "○"}</span>
                 </div>
                <div style="margin-top:12px">
                  <button data-reset style="pointer-events:auto;cursor:pointer;
                      padding:6px 12px;border-radius:8px;border:1px solid #22d3ee;
                      background:#12324a;color:#fff;font-size:13px;font-weight:600">
                      Reset to defaults</button>
                </div>
              </div>`;
        this.querySelectorAll("[data-cat]").forEach((el) => {
            const id = el.getAttribute("data-cat");
            el.addEventListener("click", () => {
                if (!this.trained.has(id))
                    return;
                const enabled = { ...store.gameSettings.get().enabled };
                enabled[id] = !(enabled[id] ?? true);
                // Never leave everything off; re-enable this trained sound.
                if (this.categories
                    .filter((c) => this.trained.has(c))
                    .every((c) => !enabled[c])) {
                    enabled[id] = true;
                }
                this.patch({ enabled });
            });
        });
        this.querySelectorAll("[data-preset]").forEach((el) => {
            const id = el.getAttribute("data-preset");
            const preset = DIFFICULTY_PRESETS.find((p) => p.id === id);
            if (!preset)
                return;
            el.addEventListener("click", () => {
                this.patch({ ...preset.settings });
            });
        });
        this.querySelectorAll("[data-set]").forEach((inp) => {
            const key = inp.getAttribute("data-set");
            inp.addEventListener("input", () => {
                const val = Number(inp.value);
                // Update the label in place, then patch the store while
                // suppressing our own re-render so the dragged input survives.
                const valEl = this.querySelector(`[data-val='${key}']`);
                if (valEl && fmts[key])
                    valEl.textContent = fmts[key](val);
                this.suppressRerender = true;
                this.patch({ [key]: val });
                this.suppressRerender = false;
            });
        });
        this.querySelector("[data-infinite-row]")?.addEventListener("click", () => {
            this.patch({ infinite: !store.gameSettings.get().infinite });
        });
        this.querySelector("[data-reset]")?.addEventListener("click", () => {
            store.gameSettings.set({ ...DEFAULT_GAME_SETTINGS });
        });
    }
}
customElements.define("ac-game-settings", AcGameSettings);
